// src/components/CustomEdge.jsx
import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useReactFlow } from "reactflow";

const LABEL_WIDTH = 100;
const LABEL_HEIGHT = 20;

function orthogonalStepPath(start, end) {
  const midY = start.y + (end.y - start.y) / 2;

  return `M${start.x},${start.y} L${start.x},${midY} L${end.x},${midY} L${end.x},${end.y}`;
}

export default function CustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  markerEnd,
  data,
}) {
  // ignore offset1/offset2 for initial placement (legacy elbow offsets)
  const { setMasterEdges, labelPos, label } = data || {};
  const label_x = labelPos?.x;
  const label_y = labelPos?.y;

  // Lazily initialize to midpoint between source and target (only on mount)
  const initialMid = () => ({
    x: typeof label_x === "number" ? label_x : (sourceX + targetX) / 2,
    y: typeof label_y === "number" ? label_y : (sourceY + targetY) / 2,
  });

  const [labelCenter, setLabelCenter] = useState(initialMid);
  const labelCenterRef = useRef(labelCenter);
  labelCenterRef.current = labelCenter;

  const labelRef = useRef(null);
  const draggingRef = useRef(false);

  // reactflow instance -> zoom conversion
  const rf = useReactFlow();
  const getZoom = useMemo(() => rf?.getZoom ?? (() => 1), [rf]);

  // compute orthogonal paths (always connect to label center)
  const path1 = orthogonalStepPath({ x: sourceX, y: sourceY }, labelCenter);
  const path2 = orthogonalStepPath(labelCenter, { x: targetX, y: targetY });

  // handle movement: use pointer events + movementX/movementY converted by zoom
  const onPointerMove = useCallback(
    (e) => {
      if (!draggingRef.current) return;
      const zoom = getZoom();
      // movementX / movementY are in screen pixels; convert to graph coords
      const dx = (e.movementX ?? 0) / zoom;
      const dy = (e.movementY ?? 0) / zoom;

      setLabelCenter((prev) => {
        const updated = { x: prev.x + dx, y: prev.y + dy };
        labelCenterRef.current = updated;
        return updated;
      });
      // stop propagation so ReactFlow doesn't interpret as panning
      e.stopPropagation?.();
    },
    [getZoom]
  );

  // cleanup function for listeners
  useEffect(() => {
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerMove); // safe no-op
    };
  }, [onPointerMove]);

  // pointerup handler to finish dragging and persist
  const finishDragAndSave = useCallback(
    (pos) => {
      if (!setMasterEdges) return;

      // Update local masterEdges state with new label position
      setMasterEdges((edges) =>
        edges.map((ed) =>
          ed.id === id ? { ...ed, label_x: pos.x, label_y: pos.y } : ed
        )
      );

      // Persist label position to backend (PATCH label_x and label_y)
      fetch(`${process.env.REACT_APP_BACKEND_URL_TOOLS}/edges/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label_x: pos.x,
          label_y: pos.y,
        }),
      }).catch((err) => console.error("Edge label position save failed:", err));
    },
    [id, setMasterEdges]
  );

  const onPointerUpWindow = useCallback(
    (e) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      // remove listeners added on pointerdown
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUpWindow);
      // save latest position
      finishDragAndSave(labelCenterRef.current);
      e?.stopPropagation?.();
    },
    [finishDragAndSave, onPointerMove]
  );

  const onPointerDown = useCallback(
    (e) => {
      // start dragging
      draggingRef.current = true;
      // capture pointer on the element if supported
      try {
        labelRef.current?.setPointerCapture?.(e.pointerId);
      } catch (_) {}
      // listen on window to track movement even if pointer leaves label area
      window.addEventListener("pointermove", onPointerMove, { passive: false });
      window.addEventListener("pointerup", onPointerUpWindow);
      e?.stopPropagation?.();
    },
    [onPointerMove, onPointerUpWindow]
  );

  // label style (same look as before)
  const labelStyle = {
    pointerEvents: "all",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #000000 0%, #cccccc 100%)",
    color: "#fff",
    padding: "6px 8px",
    border: "1px solid #444",
    borderRadius: 4,
    fontSize: "0.65rem",
    fontWeight: "normal",
    cursor: "grab",
    userSelect: "none",
    whiteSpace: "pre-line",
    maxWidth: LABEL_WIDTH,
    height: LABEL_HEIGHT,
    textAlign: "center",
    boxShadow: "0 0 5px rgba(0,0,0,0.3)",
  };

  return (
    <>
      <path
        id={`${id}-source-label`}
        className="react-flow__edge-path"
        d={path1}
        style={{ stroke: "#fff", strokeWidth: 3, fill: "none" }}
      />
      <path
        id={`${id}-label-target`}
        className="react-flow__edge-path"
        d={path2}
        markerEnd={markerEnd}
        style={{ stroke: "#fff", strokeWidth: 3, fill: "none" }}
      />

      <foreignObject
        width={LABEL_WIDTH}
        height={LABEL_HEIGHT}
        x={labelCenter.x - LABEL_WIDTH / 2}
        y={labelCenter.y - LABEL_HEIGHT / 2}
        pointerEvents="all"
        style={{ overflow: "visible" }}
      >
        <div
          ref={labelRef}
          style={labelStyle}
          onPointerDown={onPointerDown}
          // show coords for quick debugging
        >
          {label}
        </div>
      </foreignObject>
    </>
  );
}
